import React, { useState, useMemo } from "react";
import { Alert, Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider, IconButton, Link, Tooltip, Typography, LinearProgress, Stepper, Step, StepLabel, StepContent, Paper } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from "react-router-dom";

// Icons
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EditIcon from "@mui/icons-material/Edit";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import PaidIcon from "@mui/icons-material/Paid";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningIcon from "@mui/icons-material/Warning";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";
import LaunchIcon from "@mui/icons-material/Launch";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";

import BaseBubble from "./BaseBubble";
import { getFileType, isMessageMine } from "../../Utils/constants";
import { styled } from "@mui/material/styles";

/* ───────────────────────── Enhanced Progress Components ───────────────────────── */

const ProgressCard = styled(Card)(({ theme, status }) => ({
  background: status === 'completed' 
    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.2)})`
    : status === 'in-progress'
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.2)})`
    : `linear-gradient(135deg, ${alpha(theme.palette.grey[500], 0.05)}, ${alpha(theme.palette.grey[500], 0.1)})`,
  borderRadius: '16px',
  border: status === 'completed' 
    ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
    : status === 'in-progress'
    ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
    : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const AttachmentCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'block',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[8],
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
}));

const StepIcon = styled(Box)(({ theme, completed, active }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: completed 
    ? theme.palette.success.main
    : active 
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  color: completed || active ? 'white' : theme.palette.grey[600],
  fontSize: '12px',
  fontWeight: 600,
}));

const ProgressChip = styled(Chip)(({ theme, variant }) => {
  const variants = {
    success: {
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    },
    warning: {
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.main,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
    },
    error: {
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    },
    info: {
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.info.main,
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
    },
  };
  
  return {
    ...variants[variant] || variants.info,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
  };
});

/* ───────────────────────── Progress Calculation ───────────────────────── */

const calculateVideoProgress = (video, crossposts = []) => {
  const steps = [
    {
      id: 'created',
      label: 'Video Assignment',
      description: 'Video slot created',
      completed: true, // Always true if video exists
      required: true,
    },
    {
      id: 'draft',
      label: 'Content Draft',
      description: 'Initial content uploaded',
      completed: !!(video.draftLink || video.submissionLink),
      required: true,
    },
    {
      id: 'links',
      label: 'Platform Links',
      description: 'Live platform links added',
      completed: !!video.liveWebhook && crossposts.every(c => !!c.liveLink),
      required: true,
    },
    {
      id: 'boost',
      label: 'Boost Codes',
      description: 'Promotional codes provided',
      completed: !!video.boostCode && crossposts.every(c => !!c.boostCode),
      required: true,
    },
    {
      id: 'timeline',
      label: 'Timeline',
      description: 'Delivery timeline met',
      completed: video.postDate ? new Date(video.postDate) <= new Date() : true,
      required: !!video.expectedDate,
    },
    {
      id: 'payment',
      label: 'Payment',
      description: 'Payment processed',
      completed: !!video.payoutComplete,
      required: true,
    },
  ];

  const requiredSteps = steps.filter(step => step.required);
  const completedSteps = requiredSteps.filter(step => step.completed);
  const currentStep = requiredSteps.findIndex(step => !step.completed);
  
  const progressPercentage = (completedSteps.length / requiredSteps.length) * 100;
  
  const status = progressPercentage === 100 
    ? 'completed' 
    : progressPercentage > 0 
    ? 'in-progress' 
    : 'not-started';

  return {
    steps: requiredSteps,
    completedCount: completedSteps.length,
    totalCount: requiredSteps.length,
    progressPercentage,
    currentStepIndex: currentStep === -1 ? requiredSteps.length - 1 : currentStep,
    status,
    nextAction: currentStep === -1 ? null : requiredSteps[currentStep].label
  };
};

/* ───────────────────────── Campaign Progress Bar Component ───────────────────────── */

const CampaignProgressBar = ({ videos = [], creatorName, campaignName, allMessages, metadata }) => {
  const theme = useTheme();
  
  /* ------------------------------------------------------------------ */
  /*  Campaign Progress Data                                            */
  /* ------------------------------------------------------------------ */
  const campaignVideos = useMemo(() => {
    // Extract video data from campaign attachments in messages
    const videoMessages = allMessages.filter(msg => 
      msg.attachments?.attachment_type === 'campaign_video'
    );
    
    const videos = [];
    videoMessages.forEach(msg => {
      const videoData = msg.attachments?.data?.video;
      const crossposts = msg.attachments?.data?.crossposts || [];
      if (videoData) {
        videos.push({
          ...videoData,
          crossposts
        });
      }
    });

    return videos;
  }, [allMessages]);

  const calculatedCreatorName = useMemo(() => {
    return metadata?.creator_name || metadata?.participants?.find(p => p.is_creator)?.name || 'Creator';
  }, [metadata]);

  const calculatedCampaignName = useMemo(() => {
    return metadata?.campaign_name || metadata?.campaign?.name || 'Campaign';
  }, [metadata]);

  const overallProgress = useMemo(() => {
    if (!videos.length && !campaignVideos.length) {
      return { 
        progressPercentage: 0, 
        status: 'not-started',
        completedSteps: 0,
        totalSteps: 0,
        videoProgresses: []
      };
    }

    const videoProgresses = (videos.length ? videos : campaignVideos)
      .filter(video => !video.parentId) // Only main videos, not crossposts
      .map(video => {
        const crossposts = (videos.length ? videos : campaignVideos).filter(v => v.parentId === video.id);
        return calculateVideoProgress(video, crossposts);
      });

    const totalSteps = videoProgresses.reduce((sum, vp) => sum + vp.totalCount, 0);
    const completedSteps = videoProgresses.reduce((sum, vp) => sum + vp.completedCount, 0);

    const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

    const status = progressPercentage === 100
      ? 'completed'
      : progressPercentage > 0
      ? 'in-progress'
      : 'not-started';

    return {
      progressPercentage,
      status,
      completedSteps,
      totalSteps,
      videoProgresses
    };
  }, [videos, campaignVideos]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return theme.palette.success.main;
      case 'in-progress': return theme.palette.primary.main;
      default: return theme.palette.grey[400];
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'in-progress': return <ScheduleIcon sx={{ fontSize: 16 }} />;
      default: return <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
        borderRadius: '20px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <TrendingUpIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              Campaign Progress: {creatorName || calculatedCreatorName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {campaignName || calculatedCampaignName}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ProgressChip 
            icon={getStatusIcon(overallProgress.status)}
            label={`${Math.round(overallProgress.progressPercentage)}% Complete`}
            variant={overallProgress.status === 'completed' ? 'success' : overallProgress.status === 'in-progress' ? 'info' : 'warning'}
          />
          <Typography variant="body2" color="text.secondary">
            {overallProgress.completedSteps}/{overallProgress.totalSteps} steps
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={overallProgress.progressPercentage}
        sx={{
          height: 12,
          borderRadius: 6,
          mb: 2,
          backgroundColor: alpha(theme.palette.grey[300], 0.3),
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${getStatusColor(overallProgress.status)}, ${alpha(getStatusColor(overallProgress.status), 0.7)})`,
            borderRadius: 6,
          }
        }}
      />

      {/* Quick status overview for each video */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {overallProgress.videoProgresses.map((vp, index) => {
          const video = videos.filter(v => !v.parentId)[index];
          return (
            <Tooltip 
              key={index}
              title={`Video ${index + 1}: ${vp.nextAction || 'Complete'}`}
            >
              <ProgressChip 
                size="small"
                label={`V${index + 1}: ${Math.round(vp.progressPercentage)}%`}
                variant={vp.status === 'completed' ? 'success' : vp.status === 'in-progress' ? 'info' : 'warning'}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
};

/* ───────────────────────── helpers ───────────────────────── */

const AddressLine = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.25),
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
}));

const GetIcon = (attachmentType, fileData) => {
  switch (attachmentType) {
    case "invoice":
      return <ReceiptLongIcon fontSize="small" />;
    case "file":
      return fileData?.content_type?.startsWith("image/")
        ? <ImageOutlinedIcon fontSize="small" />
        : <InsertDriveFileOutlinedIcon fontSize="small" />;
    case "campaign":
      return <CampaignOutlinedIcon fontSize="small" />;
    case "roster":
      return <GroupOutlinedIcon fontSize="small" />;
    case "mediakit":
      return <DescriptionOutlinedIcon fontSize="small" />;
    case "creator":
      return <PersonOutlinedIcon fontSize="small" />;
    case "address":
      return <LocalShippingIcon fontSize="small" />;
    case "campaign_video":
      return <MovieCreationIcon fontSize="small" />;
    case "casting_call":
      return <CampaignOutlinedIcon fontSize="small" />;
    default:
      return <InsertDriveFileOutlinedIcon fontSize="small" />;
  }
};

const isImageFile = (fileData) =>
  !!fileData && getFileType(fileData.file_url) === "image";

/** Check whether the video (incl. all cross‑posts) is ready for payment. */
const paymentReady = (videoWithCross) =>
  !!videoWithCross.liveLink &&
  !!videoWithCross.boostCode &&
  videoWithCross.crossposts.every(
    (c) => !!c.liveLink && !!c.boostCode
  ) &&
  !videoWithCross.payoutComplete;

/* ───────────────────────── Image Modal Component ───────────────────────── */

function ImageModal({ open, onClose, imageUrl, filename }) {
  const [isZoomed, setIsZoomed] = useState(false);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'image';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogActions sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
        <Tooltip title="Download">
          <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isZoomed ? "Zoom Out" : "Zoom In"}>
          <IconButton onClick={() => setIsZoomed(!isZoomed)} sx={{ color: 'white' }}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogActions>
      
      <DialogContent 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '70vh',
          overflow: isZoomed ? 'auto' : 'hidden'
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={filename || "Attachment"}
          sx={{
            maxWidth: isZoomed ? 'none' : '100%',
            maxHeight: isZoomed ? 'none' : '100%',
            width: isZoomed ? 'auto' : 'auto',
            height: isZoomed ? 'auto' : 'auto',
            objectFit: 'contain',
            borderRadius: 1,
            cursor: isZoomed ? 'grab' : 'pointer',
            transition: 'transform 0.3s ease',
            transform: isZoomed ? 'scale(1.5)' : 'scale(1)',
            '&:active': {
              cursor: isZoomed ? 'grabbing' : 'pointer',
            }
          }}
          onClick={() => setIsZoomed(!isZoomed)}
        />
      </DialogContent>
      
      {filename && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
            {filename}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}

/* ───────────────────────── Enhanced attachment renderer ───────────────────────── */

function RenderAttachment({
  att,
  convMeta,
  onEditFile,
  onVideoLink,
  isUser,
  onCompletePayment,
  onRequestPayment,
  completePaymentLoading,
  requestPaymentLoading,
}) {

    console.group('🔍 RenderAttachment Debug');
    console.log('Raw attachment object:', att);
  console.log('Attachment type:', att?.attachment_type);
  console.log('Has casting_call field?', 'casting_call' in (att || {}));
  console.log('casting_call value:', att?.casting_call);
  console.log('casting_call_id value:', att?.casting_call_id);
  console.log('data field value:', att?.data);
  console.groupEnd();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const theme = useTheme();

  const attachmentType = att.attachment_type;
  const fileData = att.file;

  console.log('🎯 Processed attachmentType:', attachmentType);
 if (attachmentType === 'casting_call') {
    console.log('🎬 CASTING CALL DETECTED!');
    console.log('🎬 Will attempt to render casting call card');
  }

  /* generic loading guard for most types */
  if (
    attachmentType !== "invoice" &&
    attachmentType !== "casting_call" &&
    !att.file &&
    !att.creator &&
    !att.roster &&
    !att.campaign &&
    !att.data
  ) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading attachment...</Typography>
      </Box>
    );
  }

  const handleImageClick = (imageUrl, filename) => {
    setSelectedImage({ url: imageUrl, filename });
    setImageModalOpen(true);
  };

  /* 1) Invoice - Enhanced card sizing */
  if (attachmentType === "invoice") {
    const invoice = att.data?.invoice_data;
    if (!invoice)
      return <Typography color="error">Invalid invoice data.</Typography>;

    return (
      <ProgressCard variant="outlined" sx={{ minWidth: 280, maxWidth: 320 }}>
        <CardHeader
          avatar={<ReceiptLongIcon color="primary" />}
          title={
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Invoice #{invoice.id ?? "----"}
            </Typography>
          }
          subheader={`PO ${invoice.po_number ?? "N/A"}`}
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem' }}>
            Amount: <strong>${invoice.amount}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem' }}>
            Client: <strong>{invoice.client_name}</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.85rem", opacity: 0.8, mt: 1 }}
          >
            Status: {invoice.invoice_status ?? "pending"}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between", px: 2, pt: 0 }}>
          <Button
            size="small"
            component={RouterLink}
            to={`/creatorinvoice/${invoice.id}`}
            variant="outlined"
          >
            View Details
          </Button>
          <Tooltip title="Share invoice">
            <IconButton
              component={RouterLink}
              to={`/creatorinvoice/${invoice.id}`}
              color="primary"
            >
              <ShareOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </ProgressCard>
    );
  }

  /* 2) File */
  if (attachmentType === "file") {
    if (!fileData)
      return <Typography variant="body2">No file data found.</Typography>;

    if (isImageFile(fileData)) {
      return (
        <>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Box
              component="img"
              src={fileData.file_url}
              alt="Attachment"
              onClick={() => handleImageClick(fileData.file_url, fileData.filename)}
              sx={{
                maxWidth: 200,
                maxHeight: 200,
                borderRadius: 2,
                cursor: "pointer",
                transition: ".3s",
                "&:hover": { boxShadow: 3, transform: "scale(1.02)" },
              }}
            />

            {!!fileData.id && (
              <Tooltip title="Edit file">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditFile?.(fileData);
                  }}
                  sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <ImageModal
            open={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            imageUrl={selectedImage?.url}
            filename={selectedImage?.filename}
          />
        </>
      );
    }

    /* non‑image */
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton component={RouterLink} to={`/files/${fileData.id}`} color="primary">
          <ShareOutlinedIcon />
        </IconButton>
        <Typography variant="body2">{fileData.filename}</Typography>
        <Chip
          label={(fileData.content_type?.split("/")[1] ?? "file").toUpperCase()}
          size="small"
          sx={{ ml: 1 }}
          color="secondary"
        />
        {!!fileData.id && (
          <Tooltip title="Edit file">
            <IconButton onClick={() => onEditFile?.(fileData)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  /* 3) Campaign - Enhanced card design */
  if (attachmentType === "campaign") {
    const campaign = att.campaign;
    const campaignName = campaign?.name ?? "Unnamed Campaign";
    const campaignId = campaign?.id ?? "";
    const campaignDescription = campaign?.description || campaign?.brief;
    const campaignBudget = campaign?.budget;
    const campaignDeadline = campaign?.deadline || campaign?.end_date;

    return (
      <AttachmentCard
        component={RouterLink}
        to={`/campaigns/${campaignId}`}
        sx={{ minWidth: 280, maxWidth: 380, textDecoration: 'none' }}
      >
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
              <CampaignOutlinedIcon />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {campaignName}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Campaign Project
            </Typography>
          }
          action={
            <Tooltip title="View Campaign">
              <IconButton size="small" sx={{ color: 'primary.main' }}>
                <LaunchIcon />
              </IconButton>
            </Tooltip>
          }
          sx={{ pb: 1 }}
        />
        
        {(campaignDescription || campaignBudget || campaignDeadline) && (
          <>
            <Divider />
            <CardContent sx={{ py: 2 }}>
              {campaignDescription && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4
                  }}
                >
                  {campaignDescription}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {campaignBudget && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PaidIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      ${campaignBudget}
                    </Typography>
                  </Box>
                )}
                
                {campaignDeadline && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(campaignDeadline).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </>
        )}
        
        <Divider />
        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Chip 
            label="Campaign" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
            View Details →
          </Typography>
        </CardActions>
      </AttachmentCard>
    );
  }

  /* 4) Creator - Enhanced card design */
  if (attachmentType === "creator") {
    const creator = att.creator;
    const creatorName = creator?.creator ?? creator?.name ?? "Unnamed Creator";
    const creatorId = creator?.creator ?? creator?.id ?? "";
    const creatorAvatar = creator?.pfphref || creator?.avatar_url;
    const creatorPlatform = creator?.platform || creator?.primary_platform;
    const creatorFollowers = creator?.followers || creator?.follower_count;
    const creatorBio = creator?.bio || creator?.description;
    const creatorLocation = creator?.location;

    return (
      <AttachmentCard
        component={RouterLink}
        to={`/creators/${creatorId}`}
        sx={{ minWidth: 280, maxWidth: 380, textDecoration: 'none' }}
      >
        <CardHeader
          avatar={
            <Avatar 
              src={creatorAvatar} 
              sx={{ width: 48, height: 48, border: `2px solid ${theme.palette.primary.main}` }}
            >
              <PersonOutlinedIcon />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {creatorName}
            </Typography>
          }
          subheader={
            <Box sx={{ mt: 0.5 }}>
              {creatorPlatform && (
                <Typography variant="body2" color="text.secondary">
                  {creatorPlatform} Creator
                </Typography>
              )}
              {creatorFollowers && (
                <Typography variant="caption" color="text.secondary">
                  {creatorFollowers.toLocaleString()} followers
                </Typography>
              )}
            </Box>
          }
          action={
            <Tooltip title="View Profile">
              <IconButton size="small" sx={{ color: 'primary.main' }}>
                <LaunchIcon />
              </IconButton>
            </Tooltip>
          }
          sx={{ pb: 1 }}
        />
        
        {(creatorBio || creatorLocation) && (
          <>
            <Divider />
            <CardContent sx={{ py: 2 }}>
              {creatorBio && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4
                  }}
                >
                  {creatorBio}
                </Typography>
              )}
              
              {creatorLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'info.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    {creatorLocation}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </>
        )}
        
        <Divider />
        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Chip 
            label="Creator" 
            size="small" 
            color="secondary" 
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
            View Profile →
          </Typography>
        </CardActions>
      </AttachmentCard>
    );
  }

  /* 5) Address */
  if (attachmentType === "address") {
    const addressData = att.data;
    if (!addressData)
      return <Typography color="error">Invalid address data.</Typography>;

    return (
      <ProgressCard variant="outlined" sx={{ maxWidth: 350 }}>
        <CardHeader avatar={<LocalShippingIcon color="primary" />} title="Delivery Address" />
        <Divider />
        <CardContent>
          <Typography variant="body1" fontWeight={600}>
            {addressData.recipient}
          </Typography>
          <AddressLine variant="body2">{addressData.address}</AddressLine>
          <AddressLine variant="body2">
            {addressData.city}, {addressData.state} {addressData.zip}
          </AddressLine>
          <AddressLine variant="body2">{addressData.country}</AddressLine>
          {addressData.instructions && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {addressData.instructions}
            </Alert>
          )}
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PhoneIcon color="primary" sx={{ fontSize: "0.9rem", mr: 0.5 }} />
            <Typography variant="body2">{addressData.phone}</Typography>
          </Box>
        </CardActions>
      </ProgressCard>
    );
  }

  /* 6) Enhanced Campaign video with progress tracking */
  if (attachmentType === "campaign_video") {
    const videoData = att.data;
    if (!videoData?.video)
      return (<Box p={2}><Typography color="error">Video data not found.</Typography></Box>);

    const video = {
      ...videoData.video,
      crossposts: videoData.crossposts ?? [],
    };
    
    const progress = calculateVideoProgress(video, video.crossposts);
    const eligible = paymentReady(video);

    const getStatusColor = () => {
      switch (progress.status) {
        case 'completed': return theme.palette.success.main;
        case 'in-progress': return theme.palette.primary.main;
        default: return theme.palette.grey[400];
      }
    };

    return (
      <ProgressCard variant="outlined" status={progress.status} sx={{ maxWidth: 420, minWidth: 350 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: getStatusColor() }}>
              <MovieCreationIcon />
            </Avatar>
          }
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Campaign Video</Typography>
              <ProgressChip 
                size="small"
                label={`${Math.round(progress.progressPercentage)}%`}
                variant={progress.status === 'completed' ? 'success' : progress.status === 'in-progress' ? 'info' : 'warning'}
              />
            </Box>
          }
          subheader={`#${String(video.id).padStart(3, "0")} • ${video.promotionPlatform} ${video.promotionType}`}
        />
        
        {/* Progress Bar */}
        <Box sx={{ px: 2, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress.progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.grey[300], 0.3),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${getStatusColor()}, ${alpha(getStatusColor(), 0.7)})`,
                borderRadius: 4,
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {progress.completedCount}/{progress.totalCount} steps complete
            </Typography>
            {progress.nextAction && (
              <Typography variant="caption" color="primary">
                Next: {progress.nextAction}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Quick status indicators */}
        <CardContent sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {progress.steps.map((step) => {
              const Icon = step.completed ? CheckCircleIcon : 
                          step.id === progress.steps[progress.currentStepIndex]?.id ? ScheduleIcon : 
                          RadioButtonUncheckedIcon;
              
              return (
                <Tooltip key={step.id} title={step.description}>
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }} />}
                    label={step.label}
                    variant={step.completed ? "filled" : "outlined"}
                    color={step.completed ? "success" : step.id === progress.steps[progress.currentStepIndex]?.id ? "primary" : "default"}
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                </Tooltip>
              );
            })}
          </Box>

          {/* Main video details */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Platform Link</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {video.liveLink ? (
                  <Link href={video.liveLink} target="_blank" sx={{ fontSize: '0.875rem' }}>
                    <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Live
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                    Pending
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Boost Code</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {video.boostCode ? (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CodeIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                    Ready
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                    Pending
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Cross-posts summary */}
          {video.crossposts.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Cross-posts ({video.crossposts.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {video.crossposts.map((crosspost, index) => (
                  <Chip
                    key={crosspost.id}
                    size="small"
                    label={crosspost.promotionPlatform}
                    color={crosspost.liveLink && crosspost.boostCode ? "success" : "warning"}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>

        <Divider />
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button 
            onClick={() => onVideoLink(video.id)}
            startIcon={<VideoLibraryIcon />}
            size="small"
          >
            Details
          </Button>

          {isUser ? (
            <Button
              variant="contained"
              color={progress.status === 'completed' ? "success" : "primary"}
              size="small"
              startIcon={<PaidIcon />}
              disabled={!eligible || completePaymentLoading}
              onClick={() => onCompletePayment?.(video.id)}
            >
              {completePaymentLoading ? "Processing..." : eligible ? "Complete Payment" : "Not Ready"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color={progress.status === 'completed' ? "success" : "primary"}
              size="small"
              startIcon={<RequestQuoteIcon />}
              disabled={!eligible || requestPaymentLoading}
              onClick={() => onRequestPayment?.(video.id)}
            >
              {requestPaymentLoading ? "Requesting..." : eligible ? "Request Payment" : "Not Ready"}
            </Button>
          )}
        </CardActions>
      </ProgressCard>
    );
  }

  /* 7) ✅ NEW: Casting Call */
  if (attachmentType === "casting_call") {
   console.log('🎬🎬🎬 INSIDE CASTING CALL SECTION');
    console.log('🎬 att object keys:', Object.keys(att || {}));
    console.log('🎬 att.casting_call:', att.casting_call);
    
    const castingCall = att.casting_call;  // ✅ Access directly!
    
    console.log('🎬 castingCall variable:', castingCall);
    console.log('🎬 castingCall is null?', castingCall === null);
    console.log('🎬 castingCall is undefined?', castingCall === undefined);
    console.log('🎬 castingCall.id:', castingCall?.id);
    
    if (!castingCall || !castingCall.id) {
    return <Typography color="error">Invalid casting call data.</Typography>;
  }

    console.log('✅✅✅ CASTING CALL IS VALID - SHOULD RENDER NOW');
    console.log('✅ Full casting call data:', castingCall);
    
    const castingCallId = castingCall.id;
    const brief = castingCall.brief || "Casting Call Opportunity";
    const callType = castingCall.call_type || 'creator';
    const budget = castingCall.budget;
    const platforms = castingCall.platforms || [];
    const workTypes = castingCall.work_types || [];
    const status = castingCall.status || 'ACTIVE';
    const publicSlug = castingCall.public_slug;
    const deadline = castingCall.deadline;
    const requirements = castingCall.requirements || [];
    const deliverables = castingCall.deliverables || [];

    const getStatusColor = () => {
      switch (status) {
        case 'ACTIVE': return theme.palette.success.main;
        case 'FILLED': return theme.palette.info.main;
        case 'CLOSED': return theme.palette.grey[500];
        default: return theme.palette.warning.main;
      }
    };

    return (
      <AttachmentCard
        component={RouterLink}
        to={publicSlug ? `/casting-calls/public/${publicSlug}` : `/castingcalls/${castingCallId}`}
        sx={{ 
          minWidth: 320, 
          maxWidth: 420, 
          textDecoration: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
        }}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(getStatusColor(), 0.15)}, ${alpha(getStatusColor(), 0.25)})`,
            borderBottom: `2px solid ${alpha(getStatusColor(), 0.3)}`,
            p: 2,
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar 
                sx={{ 
                  bgcolor: getStatusColor(),
                  width: 48, 
                  height: 48,
                  boxShadow: `0 4px 12px ${alpha(getStatusColor(), 0.3)}`,
                }}
              >
                <CampaignOutlinedIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  🎬 Casting Call
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary',
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontWeight: 600,
                }}>
                  {callType === 'creator' ? 'Creator Opportunity' : 'General Work'}
                </Typography>
              </Box>
            </Box>

            <ProgressChip
              size="small"
              label={status}
              variant={status === 'ACTIVE' ? 'success' : status === 'FILLED' ? 'info' : 'warning'}
              icon={status === 'ACTIVE' ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <ScheduleIcon sx={{ fontSize: 14 }} />}
            />
          </Box>
        </Box>

        <CardContent sx={{ py: 2.5 }}>
          {/* Brief description */}
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2,
              fontWeight: 500,
              color: 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
            }}
          >
            {brief}
          </Typography>

          {/* Key details grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: budget && deadline ? '1fr 1fr' : '1fr',
            gap: 2, 
            mb: 2,
            p: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}>
            {budget && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <PaidIcon sx={{ fontSize: 14 }} />
                  Budget
                </Typography>
                <Typography variant="body2" fontWeight={700} color="success.main">
                  Up to ${budget.toLocaleString()}
                </Typography>
              </Box>
            )}
            
            {deadline && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14 }} />
                  Deadline
                </Typography>
                <Typography variant="body2" fontWeight={700} color="warning.main">
                  {new Date(deadline).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Platforms/Work Types */}
          {callType === 'creator' && platforms.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                Platforms
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {platforms.slice(0, 4).map((platform, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={platform}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22,
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                ))}
                {platforms.length > 4 && (
                  <Chip
                    size="small"
                    label={`+${platforms.length - 4} more`}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {callType === 'general' && workTypes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                Work Types
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {workTypes.slice(0, 4).map((workType, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={workType.replace(/_/g, ' ')}
                    color="secondary"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22, 
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                ))}
                {workTypes.length > 4 && (
                  <Chip
                    size="small"
                    label={`+${workTypes.length - 4} more`}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Requirements preview */}
          {requirements.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                Key Requirements
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {requirements.slice(0, 3).map((req, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={req}
                    icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                    sx={{ 
                      fontSize: '0.65rem', 
                      height: 20,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                      '& .MuiChip-label': { px: 0.5 },
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
                ))}
                {requirements.length > 3 && (
                  <Chip
                    size="small"
                    label={`+${requirements.length - 3}`}
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Deliverables preview */}
          {deliverables.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                Deliverables ({deliverables.length})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WorkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {deliverables.slice(0, 2).join(', ')}
                  {deliverables.length > 2 && ` +${deliverables.length - 2} more`}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
        
        <Divider />
        
        {/* Footer with action */}
        <CardActions 
          sx={{ 
            justifyContent: 'space-between', 
            px: 2, 
            py: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={status === 'ACTIVE' ? "🟢 Accepting Applications" : status === 'FILLED' ? "✅ Filled" : "⏸️ Closed"} 
              size="small" 
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                bgcolor: alpha(getStatusColor(), 0.1),
                color: getStatusColor(),
                border: `1px solid ${alpha(getStatusColor(), 0.3)}`,
              }}
            />
          </Box>
          
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              View Full Details
              <LaunchIcon sx={{ fontSize: 14 }} />
            </Typography>
          </Box>
        </CardActions>
      </AttachmentCard>
    );
  }

  /* fallback */
  return (
    <Typography variant="body2" color="text.secondary">
      Unknown attachment type: {attachmentType}
    </Typography>
  );
}

/* ───────────────────────── Enhanced Attachment Bubble ───────────────────────── */

export default function AttachmentBubble({
  msg,
  userId,
  isTcc,
  isUser,
  metadata,
  handleDeleteMessage,
  deleteMessageLoading,
  onEditFile,
  handleVideoLink,
  onCompletePayment,
  onRequestPayment,
  completePaymentLoading,
  requestPaymentLoading,
}) {
  const isUserMessage = isMessageMine(userId, msg);
  const att = msg.attachments;
  if (!att) return null;

  const attachmentType = att.attachment_type ?? "unknown";

  // Get sender info - same logic as ChatBubble
  const senderId = `${msg.sender_id || msg.creator_id}`;
  let senderName = '';
  if (msg.sender_id) {
    senderName = `${msg.sender?.first_name || 'N/A'} ${msg.sender?.last_name || 'N/A'}`;
  } else if (msg.creator_id) {
    senderName = msg.creator_id;
  } else {
    senderName = 'Blitz System';
  }
  
  let avatarUrl = 
    msg.sender?.pfphref   
    || msg.sender?.phphref 
    || msg.creator?.pfphref
    || '';
  
  // Fallback to metadata participants if needed
  if (!avatarUrl && metadata?.participants) {
    const participant = metadata.participants.find(p => p.id === senderId);
    avatarUrl = participant?.pfp || '';
  }
  
  const isNumeric = /^\d+$/.test(senderId);
  const profileLink = isNumeric ? `/profile/${senderId}` : `/profile/${senderId}`;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
        mb: 1.5,
        maxWidth: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUserMessage ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 2,
          maxWidth: '85%',
          width: 'auto'
        }}
      >
        <Avatar 
          src={avatarUrl || '/default-avatar.png'}
          alt={senderName}
          sx={{ 
            width: 40, 
            height: 40,
            alignSelf: 'flex-start',
            mt: 0.5
          }}
        />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: isUserMessage ? 'flex-end' : 'flex-start',
          maxWidth: '100%',
          minWidth: 0
        }}>
          <BaseBubble
            message={msg}
            isUserMessage={isUserMessage}
            isTcc={isTcc}
            handleDeleteMessage={() => handleDeleteMessage(msg.id)}
            deleteMessageLoading={deleteMessageLoading}
            customColors
            // Only remove padding for complex attachments that handle their own spacing
            padding={["address", "campaign_video", "campaign", "creator", "casting_call"].includes(attachmentType) ? 0 : undefined}
            sx={{
              minWidth: '120px',
              maxWidth: '100%'
            }}
          >
            {/* Sender name for non-user messages */}
            {!isUserMessage && (
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 'bold',
                  mb: 0.5
                }}
              >
                <Link 
                  href={profileLink} 
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {senderName}
                </Link>
              </Typography>
            )}

            <RenderAttachment
              att={att}
              convMeta={metadata}
              onEditFile={onEditFile}
              onVideoLink={handleVideoLink}
              isUser={isUser}
              onCompletePayment={onCompletePayment}
              onRequestPayment={onRequestPayment}
              completePaymentLoading={completePaymentLoading}
              requestPaymentLoading={requestPaymentLoading}
            />
          </BaseBubble>
        </Box>
      </Box>
    </Box>
  );
}

// Export the progress bar component for use in campaign screens
export { CampaignProgressBar };